import { useState } from "react";
import { toast } from "sonner";
import { Mic, Upload, Loader2, Sparkles, ShieldCheck, ShieldX, Camera } from "lucide-react";
import { useApi, useFetch, errMsg } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Tick, AadhaarVerify, IdVerify } from "./Kyc";
import { CameraCapture } from "./CameraCapture";
import { DateField } from "./DateField";
import { enqueue, isOffline, verhoeffOk } from "../lib/offline";
import { offlineNow } from "../lib/idb";
import { useStraighten } from "./StraightenDialog";

const SKILLS = ["UNSKILLED", "SEMI_SKILLED", "SKILLED", "HIGHLY_SKILLED"];

const speak = (onText) => {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return toast.error("Voice input isn't supported in this browser");
  const r = new SR();
  r.lang = "en-IN";
  r.onresult = (e) => onText(e.results[0][0].transcript);
  r.onerror = () => toast.error("Couldn't hear that, try again");
  r.start();
  toast.message("Listening…");
};

const Field = ({ label, k, f, set, voice, digits, tick, ...p }) => (
  <label className="grid gap-1.5"><span className="text-xs text-slate-400 flex items-center gap-1.5">{label}<Tick ok={tick} testid={`wiz-tick-${k}`} /></span>
    <div className="flex gap-2">
      <input className="c-input" value={f[k] || ""} onChange={(e) => set(k, digits ? e.target.value.replace(/\D/g, "") : e.target.value)} data-testid={`wiz-${k}`} {...p} />
      {voice && <button type="button" onClick={() => speak((txt) => set(k, digits ? txt.replace(/\D/g, "").slice(0, 12) : txt))} data-testid={`wiz-voice-${k}`}
        className="shrink-0 h-10 w-10 grid place-items-center rounded-lg border border-[#1F2A3E] text-slate-400 hover:text-[#00E599] hover:border-[#00E599]/40"><Mic size={15} /></button>}
    </div>
  </label>
);

const Drop = ({ label, onFile, busy, file, testid, maxMb = 5, images }) => (
  <label className="c-surface c-grid-bg border-dashed flex flex-col items-center justify-center gap-2 p-5 cursor-pointer text-center hover:!border-[#00E599]/50"
    onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }} data-testid={testid}>
    {busy ? <Loader2 className="animate-spin text-[#00E599]" size={20} /> : <Upload size={20} className="text-[#00E599]" />}
    <span className="text-xs">{file || label}</span><span className="text-[10px] text-slate-500">{images ? "JPG / PNG" : "PDF / JPG / PNG"} · max {maxMb} MB</span>
    <input type="file" hidden accept={images ? ".jpg,.jpeg,.png" : ".pdf,.jpg,.jpeg,.png"} onChange={(e) => { onFile(e.target.files[0]); e.target.value = ""; }} data-testid={`${testid}-input`} />
  </label>
);

const CamDrop = ({ w, mode, onFile, ...p }) => (
  <div className="grid gap-1.5">
    <Drop onFile={(x) => (mode === "photo" ? onFile(x) : w.straighten(x, mode, onFile))} {...p} />
    <button type="button" onClick={() => w.setCam({ mode, fn: onFile })} className="inline-flex items-center justify-center gap-1.5 rounded-md border border-[#1F2A3E] py-1.5 text-xs text-[#00E599] hover:bg-[#1A2234]" data-testid={`${p.testid}-camera-btn`}>
      <Camera size={13} />Take with camera</button>
  </div>);

const Box = ({ title, testid, children }) => (
  <div className="grid gap-3 rounded-lg border border-[#1F2A3E] p-3" data-testid={testid}><span className="text-xs font-semibold text-slate-300">{title}</span>{children}</div>);

const LangSelect = ({ f, set }) => (
  <label className="grid gap-1.5"><span className="text-xs text-slate-400">Worker's language (consent message, printed forms)</span>
    <select className="c-input" value={f.preferred_language || ""} onChange={(e) => set("preferred_language", e.target.value || null)} data-testid="wiz-language">
      <option value="">English</option><option value="hi">हिन्दी</option><option value="gu">ગુજરાતી</option><option value="mr">मराठी</option><option value="te">తెలుగు</option></select></label>);

const Step1 = ({ w }) => {
  const { f, set, setA, files, busy, upload, readDoc, A, sites, vendors, vendor, check, setCheck, validate, setK, next } = w;
  return (
    <form onSubmit={next} className="grid gap-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="grid gap-1.5"><span className="text-xs text-slate-400">Site</span>
          <select className="c-input" value={f.site_id || ""} onChange={(e) => { const s = (sites || []).find((x) => x.id === e.target.value); w.setF((p) => ({ ...p, site_id: e.target.value, department: null, ...(s?.vendor_id && !p.vendor_id ? { vendor_id: s.vendor_id } : {}) })); }} data-testid="wiz-site">
            <option value="">Select site…</option>{(sites || []).filter((s) => !["PENDING", "REJECTED"].includes(s.status)).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
        {(sites || []).find((s) => s.id === f.site_id)?.departments?.length > 0 && (
          <label className="grid gap-1.5"><span className="text-xs text-slate-400">Department</span>
            <select className="c-input" value={f.department || ""} onChange={(e) => set("department", e.target.value || null)} data-testid="wiz-department">
              <option value="">Select department…</option>{sites.find((s) => s.id === f.site_id).departments.map((d) => <option key={d} value={d}>{d}</option>)}</select></label>)}
        <label className="grid gap-1.5"><span className="text-xs text-slate-400">Contractor *</span>
          <select className="c-input" required value={f.vendor_id || ""} onChange={(e) => set("vendor_id", e.target.value)} data-testid="wiz-vendor">
            <option value="">Select contractor…</option>{(vendors || []).map((v) => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}</select></label>
      </div>
      {vendor && <div className="text-[11px] font-mono text-slate-400 rounded-lg bg-[#0B1017] border border-[#1F2A3E] p-3" data-testid="wiz-contractor-details">
        CLRA licence {vendor.clra_license_number || "—"} · EPF {vendor.epf_registration_number} · ESIC {vendor.esic_registration_number} · PAN {vendor.pan_number}</div>}
      <div className="grid sm:grid-cols-3 gap-3">
        <CamDrop w={w} mode="card" label="Aadhaar front (required)" file={files.FRONT} busy={busy === "FRONT"} onFile={(x) => upload(x, "FRONT")} testid="wiz-aadhaar-front" />
        <CamDrop w={w} mode="card" label="Aadhaar back (optional, better address)" file={files.BACK} busy={busy === "BACK"} onFile={(x) => upload(x, "BACK")} testid="wiz-aadhaar-back" />
        <CamDrop w={w} mode="photo" label="Passport-size photo (required)" images maxMb={10} file={files.PHOTO} busy={busy === "PHOTO"} onFile={(x) => readDoc(x, "PHOTO", 10)} testid="wiz-photo" />
      </div>
      <p className="text-[11px] text-slate-500 flex items-center gap-1.5"><Sparkles size={12} className="text-[#00E599]" />AI fills in only the fields it reads with high confidence.</p>
      <Field label="Full name as per Aadhaar *" k="worker_full_name" f={f} set={setA} voice required minLength={2} tick={A?.name_ok} />
      <Field label="Father's name" k="father_name" f={f} set={set} voice />
      <div className="grid sm:grid-cols-2 gap-3">
        <DateField label="Date of birth" k="dob" f={f} set={setA} fromYear={1940} toYear={new Date().getFullYear() - 14} tick={<Tick ok={A?.dob_ok ?? undefined} testid="wiz-tick-dob" />} />
        <label className="grid gap-1.5"><span className="text-xs text-slate-400">Gender</span>
          <select className="c-input" value={f.gender || ""} onChange={(e) => set("gender", e.target.value)} data-testid="wiz-gender">
            <option value="">Select…</option><option>MALE</option><option>FEMALE</option><option>OTHER</option></select></label>
      </div>
      <div className="grid gap-1.5">
        <Field label="Aadhaar number *" k="aadhaar_number" f={f} set={(k, v) => { setA(k, v); setCheck(null); }} voice digits required maxLength={12} minLength={12} tick={A?.number_ok} />
        <div className="flex items-center gap-3">
          <button type="button" onClick={validate} className="text-xs text-[#00E599] hover:underline" data-testid="wiz-aadhaar-validate">Check Verhoeff checksum</button>
          {check && <span className={`text-xs flex items-center gap-1 ${check.valid ? "text-emerald-400" : "text-rose-400"}`} data-testid="wiz-aadhaar-result">
            {check.valid ? <ShieldCheck size={12} /> : <ShieldX size={12} />}{check.message}</span>}
        </div>
      </div>
      <label className="grid gap-1.5"><span className="text-xs text-slate-400 flex items-center gap-1.5">Address as per Aadhaar<Tick ok={A?.address_ok ?? undefined} testid="wiz-tick-address" /></span>
        <textarea className="c-input min-h-[64px]" value={f.address || ""} onChange={(e) => setA("address", e.target.value)} data-testid="wiz-address" /></label>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Worker's mobile (WhatsApp)" k="mobile" f={f} set={set} digits maxLength={10} />
        <LangSelect f={f} set={set} />
      </div>
      <AadhaarVerify f={f} result={A} onResult={(r) => setK("AADHAAR", r)} />
      <Button type="submit" data-testid="wiz-next" className="bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold">Next</Button>
    </form>);
};

const Step2 = ({ w }) => {
  const { f, set, setP, setE, files, busy, readDoc, kyc, setK, submit, setStep } = w;
  return (
    <form onSubmit={submit} className="grid gap-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="grid gap-1.5"><span className="text-xs text-slate-400">Category *</span>
          <select className="c-input" value={f.skill_category} onChange={(e) => set("skill_category", e.target.value)} data-testid="wiz-skill">
            {SKILLS.map((s) => <option key={s}>{s}</option>)}</select></label>
        <Field label="Wage zone *" k="state_zone_code" f={f} set={set} required placeholder="MH-ZONE1" />
        <Field label="Daily wage (₹, blank = Wage Master)" k="daily_wage" f={f} set={set} type="number" min="0" step="0.01" />
        <DateField label="Date of joining" k="date_of_joining" f={f} set={set} fromYear={2000} />
      </div>
      <Box title="UAN details" testid="wiz-uan-section">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="UAN (12 digits)" k="uan_number" f={f} set={set} digits maxLength={12} />
          <Field label="PAN" k="pan_number" f={f} set={setP} maxLength={10} tick={kyc.PAN?.number_ok} />
        </div>
        <CamDrop w={w} mode="card" label={f.uan_number ? "PAN card photo (required for UAN)" : "PAN card photo"} file={files.PAN} busy={busy === "PAN"} onFile={(x) => readDoc(x, "PAN", 5, { pan_number: "pan_number" })} testid="wiz-pan-card" />
        {kyc.PAN && <p className="text-[11px] text-slate-400 flex items-center gap-1.5" data-testid="wiz-pan-name">Name on PAN <Tick ok={kyc.PAN.name_ok} testid="wiz-tick-pan-name" /> {kyc.PAN.official_name}</p>}
        <IdVerify kind="PAN" number={f.pan_number} name={f.worker_full_name} result={kyc.PAN} onResult={(r) => setK("PAN", r)} />
      </Box>
      <Box title="ESIC details" testid="wiz-esic-section">
        <Field label="ESIC IP number (10 digits)" k="esic_ip_number" f={f} set={setE} digits maxLength={10} tick={kyc.ESIC?.number_ok} />
        <CamDrop w={w} mode="card" label="ESIC card photo (or type the number above)" file={files.ESIC_CARD} busy={busy === "ESIC_CARD"} onFile={(x) => readDoc(x, "ESIC_CARD", 5, { esic_ip_number: "esic_ip_number" })} testid="wiz-esic-card" />
        {kyc.ESIC && <p className="text-[11px] text-slate-400 flex items-center gap-1.5" data-testid="wiz-esic-name">Name on ESIC <Tick ok={kyc.ESIC.name_ok} testid="wiz-tick-esic-name" /> {kyc.ESIC.official_name}</p>}
        <IdVerify kind="ESIC" number={f.esic_ip_number} name={f.worker_full_name} result={kyc.ESIC} onResult={(r) => setK("ESIC", r)} />
      </Box>
      <Box title="Bank details" testid="wiz-bank-section">
        <CamDrop w={w} mode="cheque" label="Cancelled cheque or passbook photo" file={files.BANK_PASSBOOK} busy={busy === "BANK_PASSBOOK"} onFile={(x) => readDoc(x, "BANK_PASSBOOK", 5, { bank_account: "bank_account", ifsc: "ifsc" })} testid="wiz-bank-photo" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Bank account no." k="bank_account" f={f} set={set} digits maxLength={18} />
          <Field label="IFSC" k="ifsc" f={f} set={set} maxLength={11} />
        </div>
      </Box>
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={() => setStep(1)} data-testid="wiz-back" className="border-[#1F2A3E] hover:bg-[#1A2234] hover:text-white">Back</Button>
        <Button type="submit" data-testid="wiz-submit" className="flex-1 bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold">Add worker</Button>
      </div>
    </form>);
};

const useWizard = ({ vendors, onClose, onSaved }) => {
  const api = useApi();
  const { data: sites } = useFetch("/sites");
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ skill_category: "UNSKILLED", upload_ids: [] });
  const [busy, setBusy] = useState(null);
  const [files, setFiles] = useState({});
  const [check, setCheck] = useState(null);
  const [kyc, setKyc] = useState({});
  const [cam, setCam] = useState(null);
  const [local, setLocal] = useState({});
  const [straighten, straightenEl] = useStraighten();
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setK = (kind, r) => setKyc((x) => ({ ...x, [kind]: r }));
  const touch = (kinds) => (k, v) => { set(k, v); setKyc((x) => { const n = { ...x }; kinds.forEach((d) => delete n[d]); return n; }); };
  const picks = (r, map) => Object.entries(map || {}).filter(([src, dst]) => r.fields?.[src]?.confidence >= 80 && !f[dst]).map(([src, dst]) => [src, dst, r.fields[src].value]);
  const keep = (file, key, entry) => {
    setLocal((x) => ({ ...x, [key]: { file, ...entry } }));
    setFiles((x) => ({ ...x, [key]: file.name }));
    toast.message("No signal. Photo saved on this phone; details are read when it uploads.");
  };
  const send = async (file, key, maxMb, path, fd, map, entry) => {
    if (!file) return;
    if (file.size > maxMb * 1024 * 1024) return toast.error(`This file is too big; the limit is ${maxMb} MB`);
    if (offlineNow()) return keep(file, key, { ...entry, map });
    setBusy(key);
    try {
      const r = (await api.post(path, fd)).data;
      setFiles((x) => ({ ...x, [key]: file.name }));
      const got = picks(r, map);
      setF((s) => ({ ...s, upload_ids: [...s.upload_ids, r.upload_id], ...Object.fromEntries(got.filter(([, dst]) => !s[dst]).map(([, dst, v]) => [dst, v])) }));
      toast.success(!map ? "Uploaded" : got.length ? `Read from photo: ${got.map(([src]) => src.replace(/_/g, " ")).join(", ")}` : "Uploaded. Nothing read clearly; please type the details.");
      setLocal((x) => { const n = { ...x }; delete n[key]; return n; });
    } catch (e) { if (isOffline(e)) keep(file, key, { ...entry, map }); else toast.error(errMsg(e)); }
    setBusy(null);
  };
  const form = (file, extra) => { const fd = new FormData(); fd.append("file", file); Object.entries(extra).forEach(([k, v]) => fd.append(k, v)); return fd; };
  const upload = (file, side) => file && send(file, side, 5, "/workers/ocr-aadhaar", form(file, { side }),
    { full_name: "worker_full_name", father_name: "father_name", dob: "dob", gender: "gender", aadhaar_number: "aadhaar_number", address: "address" }, { side });
  const readDoc = (file, kind, maxMb, map) => file && send(file, kind, maxMb, "/kyc/read", form(file, { kind }), map, { kind });
  const validate = async () => {
    if (offlineNow()) { const ok = verhoeffOk(f.aadhaar_number || ""); return setCheck({ valid: ok, message: ok ? "Checksum OK (checked on this phone)" : "Aadhaar number fails the checksum" }); }
    try { setCheck((await api.post("/aadhaar/validate", { aadhaar_number: f.aadhaar_number || "" })).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const next = (e) => {
    e.preventDefault();
    if (!files.FRONT) return toast.error("Upload the Aadhaar front first");
    if (!files.PHOTO) return toast.error("Upload the worker's passport-size photo");
    setStep(2);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (f.uan_number && !files.PAN) return toast.error("Upload the PAN card photo for the UAN details");
    const body = { ...f, kyc_ids: Object.values(kyc).map((r) => r.id), daily_wage: f.daily_wage ? Number(f.daily_wage) : null, site_id: f.site_id || null, department: f.department || null };
    const queue = async () => {
      await enqueue("worker", `New worker · ${f.worker_full_name}`, { body, files: Object.values(local) });
      toast.success(!offlineNow() ? "Worker saved. Uploading the photos now…" : "No signal. Worker saved on this phone; it uploads automatically when you're back online.");
      onClose();
    };
    if (offlineNow() || Object.keys(local).length) return queue();
    try {
      const r = (await api.post("/workers", body)).data;
      toast.success("Worker added"); (r?.warnings || []).forEach((w) => toast.warning(w, { duration: 10000 })); onSaved(); onClose();
    } catch (er) { if (isOffline(er)) queue(); else toast.error(errMsg(er)); }
  };
  return { f, setF, set, setA: touch(["AADHAAR", "PAN", "ESIC"]), setP: touch(["PAN"]), setE: touch(["ESIC"]), files, busy, upload, readDoc, kyc, A: kyc.AADHAAR, setK,
    sites, vendors, vendor: (vendors || []).find((v) => v.id === f.vendor_id), check, setCheck, validate, next, submit, step, setStep, cam, setCam, straighten, straightenEl };
};

export const AddWorkerWizard = (props) => {
  const w = useWizard(props);
  return (
    <Dialog open onOpenChange={props.onClose}>
      <DialogContent className="bg-[#121824] border-[#1F2A3E] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-worker-wizard">
        <DialogHeader><DialogTitle className="font-display text-xl">{`Add worker · Step ${w.step} of 2`}</DialogTitle>
          <p className="text-xs text-slate-400">{w.step === 1 ? "Aadhaar, photo & personal details" : "Employment, UAN, ESIC & bank details"}</p></DialogHeader>
        {w.step === 1 ? <Step1 w={w} /> : <Step2 w={w} />}
        {w.straightenEl}
        {w.cam && <CameraCapture mode={w.cam.mode} onPhoto={w.cam.fn} onClose={() => w.setCam(null)} />}
      </DialogContent>
    </Dialog>
  );
};

export const BulkImport = ({ vendors, onClose, onSaved }) => {
  const api = useApi();
  const [vendorId, setVendorId] = useState(vendors?.[0]?.id || "");
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const template = () => {
    const csv = "worker_full_name,aadhaar_number,uan_number,esic_ip_number,skill_category,state_zone_code,site_name,father_name,dob,gender,mobile,daily_wage,date_of_joining\nAsha Pawar,234123412346,,,UNSKILLED,MH-ZONE1,Andheri Plant,Ganesh Pawar,1995-02-10,FEMALE,9876543210,480,2026-01-05\n";
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "comply369_worker_import.csv"; a.click();
  };
  const go = async () => {
    const fd = new FormData(); fd.append("vendor_id", vendorId); fd.append("file", file);
    setBusy(true);
    try { const r = (await api.post("/workers/import", fd)).data; setRes(r); toast.success(`${r.created} workers added`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#121824] border-[#1F2A3E] max-w-lg" data-testid="bulk-import-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Import worker roster (CSV)</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">Each Aadhaar is checked (Verhoeff + duplicates) and stored encrypted. Rows with problems are skipped and listed below.</p>
        <select className="c-input" value={vendorId} onChange={(e) => setVendorId(e.target.value)} data-testid="bulk-vendor-select">
          {(vendors || []).map((v) => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}</select>
        <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} data-testid="bulk-file-input" className="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[#1A2234] file:px-3 file:py-2 file:text-slate-200" />
        <div className="flex gap-2">
          <Button variant="outline" onClick={template} data-testid="bulk-template-btn" className="border-[#1F2A3E] hover:bg-[#1A2234] hover:text-white">Template</Button>
          <Button onClick={go} disabled={!file || !vendorId || busy} data-testid="bulk-submit-btn" className="flex-1 bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold">{busy ? "Importing…" : "Import"}</Button>
        </div>
        {res && <div className="rounded-lg border border-[#1F2A3E] bg-[#0B1017] p-3 text-xs font-mono max-h-48 overflow-y-auto" data-testid="bulk-result">
          {res.created}/{res.total_rows} added{res.errors.map((e) => <div key={e} className="text-rose-400 mt-1">{e}</div>)}</div>}
      </DialogContent>
    </Dialog>
  );
};
